import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { Button } from 'reactstrap';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import type {
  CalibrationComment,
  CalibrationRecommendation,
  UpdateCalibrationCommentPayload,
} from '../types';
import { RecommendationInput } from './RecommendationInput';

export type CommentEditFormProps = {
  comment: CalibrationComment;
  editError: string | null;
  isSubmitting: boolean;
  onSubmit: (payload: UpdateCalibrationCommentPayload) => void;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  formatMessage: IntlShape['formatMessage'];
};

export const CommentEditForm: React.FC<CommentEditFormProps> = ({
  comment,
  editError,
  isSubmitting,
  onSubmit,
  onCancel,
  onDirtyChange,
  formatMessage,
}) => {
  // Only root comments carry a recommendation
  const isRootComment = !comment.parent_comment_id;

  const object = useMemo(
    () => ({
      comment: comment.comment,
      recommendation: comment.recommendation ?? undefined,
    }),
    [comment.comment, comment.recommendation]
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.RICH_TEXT_EDITOR,
        name: 'comment',
        autoFocus: false,
        placeholder: formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.edit_label',
          defaultMessage: 'Edit comment',
        }),
        config: { heightMin: 120 },
      },
      ...(isRootComment
        ? [
            {
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              name: 'recommendation',
              label: (
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.comments.recommendation_label"
                  defaultMessage="Recommendation"
                />
              ),
              component: RecommendationInput,
            },
          ]
        : []),
    ],
    [formatMessage, isRootComment]
  );

  const callback = useCallback(
    (
      data?: { comment: string; recommendation?: CalibrationRecommendation },
      error?: unknown
    ) => {
      if (error || !data) {
        return;
      }
      onSubmit({
        comment: data.comment,
        ...(isRootComment && { recommendation: data.recommendation ?? null }),
      });
    },
    [isRootComment, onSubmit]
  );

  const renderForm = useCallback(
    (children: React.ReactNode) => (
      <div className="mt-2">
        {children}
        {editError && <div className="text-danger small mb-3">{editError}</div>}
        <div className="d-flex align-items-center gap-3">
          <Button
            color="primary"
            size="sm"
            type="submit"
            disabled={isSubmitting}
          >
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.comments.save_edit"
              defaultMessage="Save"
            />
          </Button>
          <Button
            color="link"
            size="sm"
            type="button"
            className="px-0"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.comments.cancel_edit"
              defaultMessage="Cancel"
            />
          </Button>
        </div>
      </div>
    ),
    [editError, isSubmitting, onCancel]
  );

  return (
    <ValidatedForm
      object={object}
      inputs={inputs}
      callback={callback}
      renderForm={renderForm}
      hideSubmitButton
      isSubmitting={isSubmitting}
      setHasUnsavedChanges={onDirtyChange}
      disableUnsavedChangesPrompt
    />
  );
};
